// ./games/crash/state/phases/DummyTickGenerator.ts

/**
 * Manages the generation of "dummy" ticks for the initial ramp-up
 * from 1.00x to 1.01x, ensuring a smooth visual start even
 * if the server doesn't send ticks in this range.
 */
export class DummyTickGenerator {
  private timeSinceLastTick: number = 0;
  private lastTickMultiplier: number = 1.0;
  private isRamping: boolean = true;

  // --- Configuration is now in one central place ---
  /** Ticks are generated at this interval (in ms) */
  public static readonly INTERVAL = 50;
  /** The multiplier target to ramp towards (exclusive) */
  public static readonly TARGET = 1.01;
  /** The value to increment by for each dummy tick */
  public static readonly INCREMENT = 0.0015;
  // ---

  /**
   * Creates a new DummyTickGenerator.
   * @param initialMultiplier The multiplier currently in the store (e.g., 1.0).
   */
  constructor(initialMultiplier: number) {
    this.lastTickMultiplier = initialMultiplier;
    this.isRamping = initialMultiplier < DummyTickGenerator.TARGET;
    console
      .log
      //   `[DummyTickGenerator] Initialized: nowX=${initialMultiplier}, isRamping=${this.isRamping}`
      ();
  }

  /**
   * Checks if the generator is still in the ramping phase.
   */
  public getIsRamping(): boolean {
    return this.isRamping;
  }

  /**
   * Processes a real tick. (For TakeOffPhase)
   * Updates internal state based on the tick.
   * @param multiplier The multiplier from the STOMP tick message.
   */
  public registerRealTick(multiplier: number) {
    this.lastTickMultiplier = multiplier;
    this.timeSinceLastTick = 0;
    if (multiplier >= DummyTickGenerator.TARGET) {
      this.isRamping = false;
    }
  }

  /**
   * Processes a real tick. (For FlyingPhase)
   * Determines if the tick should be processed (i.e., not ignored).
   * @param multiplier The multiplier from the STOMP tick message.
   * @returns 'true' if the tick should be processed by the store.
   */
  public shouldProcessRealTick(multiplier: number): boolean {
    if (this.isRamping) {
      if (multiplier >= DummyTickGenerator.TARGET) {
        // This is the first *real* tick >= 1.01. Process it and stop ramping.
        console
          .log
          //   `[DummyTickGenerator] Ramping active. Received significant tick: ${multiplier}x. Stopping ramp.`
          ();
        this.isRamping = false;
        this.lastTickMultiplier = multiplier;
        this.timeSinceLastTick = 0;
        return true; // Process this tick
      } else {
        // This is an intermediate tick < 1.01 (e.g., 1.008). Ignore it.
        console
          .log
          //   `[DummyTickGenerator] Ramping active. Ignoring intermediate tick: ${multiplier}x.`
          ();
        return false; // Ignore this tick
      }
    }

    // Ramping is finished. Process all ticks normally.
    this.lastTickMultiplier = multiplier;
    this.timeSinceLastTick = 0;
    return true; // Process this tick
  }

  /**
   * Main update loop, called by a phase's update() method.
   * @param deltaTime The time elapsed since the last frame.
   * @param currentMultiplierInStore The current 'nowX' value from the zustand store.
   * @returns A new dummy multiplier if one should be set, otherwise null.
   */
  public update(
    deltaTime: number,
    currentMultiplierInStore: number
  ): number | null {
    if (!this.isRamping) {
      return null; // Not ramping, do nothing
    }

    this.timeSinceLastTick += deltaTime;

    // Stop if the store multiplier has somehow passed the target
    if (currentMultiplierInStore >= DummyTickGenerator.TARGET) {
      this.isRamping = false;
      return null;
    }

    // Check if the interval has passed
    if (this.timeSinceLastTick >= DummyTickGenerator.INTERVAL) {
      this.timeSinceLastTick = 0; // Reset timer

      const newDummyMultiplier =
        currentMultiplierInStore + DummyTickGenerator.INCREMENT;

      // Check if the new value hits or exceeds the target
      if (newDummyMultiplier >= DummyTickGenerator.TARGET) {
        // If it does, stop ramping and *do not* generate this tick.
        this.isRamping = false;
        return null;
      }

      // Check if this new tick is valid (i.e., it's progressing)
      if (
        newDummyMultiplier > this.lastTickMultiplier &&
        newDummyMultiplier > currentMultiplierInStore
      ) {
        console
          .log
          //   `[DummyTickGenerator] Generating dummy tick: ${newDummyMultiplier.toFixed(3)}x`
          ();
        // Update internal state so the next check is correct
        this.lastTickMultiplier = newDummyMultiplier;
        return newDummyMultiplier;
      }
    }

    return null; // No tick generated this frame
  }
}
